import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { FormattedMessage, useIntl } from 'react-intl';
import setWith from 'lodash/setWith';
import clone from 'lodash/clone';
import findIndex from 'lodash/findIndex';
import find from 'lodash/find';

import Form from '../../../../../components/Form';
import Button from '../../../../../components/Button';
import Icon from '../../../../../components/Icon';

import messages from '../messages';

import '../../styles.css';

const textFieldList = ({ values, onChange, disabled }) => {
  const intl = useIntl();
  const [customFields, setCustomFields] = useState(
    values.length ?
      values.map((name) => ({
        value: name,
        key: `${uuid()}`,
      })) :
      [{
        value: '',
        key: `${uuid()}`,
      }],
  );
  const [focusedCustomFieldKey, setFocusedCustomFieldKey] = useState(null);

  const addEmptyCustomField = useCallback(() => {
    const key = `${uuid()}`;
    const nextCustomFields = [...customFields].concat({
      value: '',
      key,
    });
    setFocusedCustomFieldKey(key);
    setCustomFields(nextCustomFields);
  }, [customFields, setCustomFields, setFocusedCustomFieldKey]);

  const onCustomItemChange = useCallback(
    (key, value) => {
      const idx = findIndex(customFields, { key });
      const nextCustomFields = [...customFields];
      setWith(nextCustomFields, `[${idx}].value`, value, clone);
      setCustomFields(nextCustomFields);
      onChange(nextCustomFields.map((item) => item.value)
        .filter(Boolean));
    },
    [customFields, setCustomFields, onChange],
  );

  // focus input element via ref callback
  const focusedCustomFieldRefCb = useCallback(
    (node) => {
      if (node) {
        node.focus();
        setFocusedCustomFieldKey(null);
      }
    },
    [setFocusedCustomFieldKey],
  );

  const removeCustomField = useCallback((key) => {
    const value = find(customFields, { key })?.value;
    const nextCustomFields = customFields.filter((item) => item.key !== key);

    setCustomFields(nextCustomFields);

    if (value) {
      const nextValues = nextCustomFields.map((item) => item.value).filter(Boolean);
      onChange(nextValues);
    }
  }, [customFields, setCustomFields, setFocusedCustomFieldKey]);

  const disabledStateRef = useRef();
  useEffect(() => {
    const normalized = !!disabled;
    if (disabledStateRef.current == null) {
      disabledStateRef.current = normalized;
    } else if (disabledStateRef.current !== normalized) {
      disabledStateRef.current = normalized;
      onChange(disabled ? [] : customFields.map((item) => item.value));
    }
  }, [disabled, customFields]);

  return (
    <div styleName="transfer-input-list">
      {customFields.map(({ value, key }) => (
        <Form.Input
          disabled={disabled}
          role="listitem"
          styleName="transfer-input-list-field"
          size="mini"
          key={key}
          fluid
          data-cy="Premium-Hem-Transfer-Application-Requirements-Custom-InputField"
          value={value}
          onChange={(e, data) => {
            onCustomItemChange(key, data.value);
          }}
        >
          <input
            ref={
              key === focusedCustomFieldKey ?
                focusedCustomFieldRefCb :
                undefined
            }
          />
          <Button
            styleName="transfer-input-list-remove-button"
            disabled={disabled}
            icon
            size="tiny"
            onClick={() => removeCustomField(key)}
            title={intl.formatMessage(
              messages.applicationRequirementsRemoveItem,
            )}
          ><Icon name="close" />
          </Button>
        </Form.Input>
      ))}
      <Button
        key="button"
        disabled={disabled}
        icon
        basic
        size="small"
        onClick={addEmptyCustomField}
        title={intl.formatMessage(messages.applicationRequirementsAddItemLabel)}
        id="addItem"
      >
        <Icon name="plus" />
        <FormattedMessage {...messages.applicationRequirementsAddItemLabel} />
      </Button>
    </div>
  );
};

textFieldList.propTypes = {
  values: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
export default textFieldList;
